import { ChevronRight } from '@mui/icons-material';
import { Box, Button, List, Stack, Typography, useMediaQuery } from '@mui/material';
import { compact } from 'lodash';
import { Fragment } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useContent } from '../../logic';
import { useGetCustomFields, useGetOrder } from '../../logic/useApiQuery';
import { useAppTheme } from '../../logic/useAppTheme';
import { getNotificationsInfo } from '../../utils';
import { isAdhocPortalOrder } from '../../utils/isAdhocPortalOrder';
import { useOrganization } from '../OrganizationProvider';
import { ClosingContactsSection, ClosingDetailsSection, ClosingDocumentsSection, ClosingTracker, LinkWithSearchParams, NotificationsSection, WiringInstructionsSection, CustomSection, TasksSection, SectionIconBadge } from '../shared';
import { getSectionsConfig, SectionConfig } from '../shared/ClosingPageSectionConfig';

export function MobileSectionLink({ sectionConfig }: {
  sectionConfig: SectionConfig;
}) {
  const { icon, name, secondaryText, badge, routeTo, searchParams, type, hideInClosingPage } = sectionConfig
  const to = routeTo || type

  if (hideInClosingPage) {
    return <></>
  }

  return (
    <Button
      component={LinkWithSearchParams}
      newSearchParams={searchParams}
      to={to}
      variant="contained"
      sx={{
        borderRadius: '20px', width: '100%', height: '69px', boxShadow: 'none',
        display: 'flex', justifyContent: 'start', pl: '37px', pr: '29px',
        fontSize: '18px', fontWeight: '400', mt: '2px', backgroundColor: 'white',
        color: 'black', '&:hover': { color: 'white' }, lineHeight: '20px',
        '.MuiButton-endIcon': { ml: 'auto', color: 'primary.main' },
        '.MuiButton-startIcon': { color: 'primary.main' },
      }}
      startIcon={<SectionIconBadge badge={badge}>{icon}</SectionIconBadge>}
      endIcon={<ChevronRight/>}
      fullWidth={true}
    >
      <Box>
        {name}
        <Typography variant="light" fontSize="small" noWrap sx={{display: 'block', fontWeight: 'bold', color: '#505050'}}>
          {secondaryText}
        </Typography>
      </Box>
    </Button>
  )
}

function MobileSectionLinkList({ sectionsConfig }: { sectionsConfig: any[] }) {
  return (
    <List sx={{
      p: 0,
    }}>
      { sectionsConfig.map(config => (
        <Fragment key={config.type} ><MobileSectionLink sectionConfig={config}/></Fragment>
      ))}
    </List>
  )
}

export function ClosingPage() {
  const { section: sectionQueryParam, id, closingId } = useParams()
  const { search } = useLocation()
  const theme = useAppTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallButNotTooSmall = useMediaQuery(theme.breakpoints.only('sm'));
  const uiContent = useContent()

  const org = useOrganization()
  
  useGetCustomFields(closingId ?? '', search ?? '')
  const { data } = useGetOrder(closingId ?? '', search ?? '')
  const globalOrderData = data!
  const order = globalOrderData?.Order!
  const [searchParams] = useSearchParams()
  const role = searchParams.get('role')
  if (!order) { return null }
  if (isAdhocPortalOrder(order)) { return null }
  const notificationsInfo = getNotificationsInfo(globalOrderData.Notifications, uiContent, globalOrderData)

  let {
    sectionsConfig,
    closingTrackerSectionConfig,
    closingDetailsSectionConfig,
  } = getSectionsConfig({ notificationsInfo, order, org })

  const sectionDetails = sectionsConfig.find(section => section.type === sectionQueryParam)
  const isStandaloneSection = sectionDetails?.isStandaloneSection || (isSmall && id !== undefined)
  const onlySection = (isSmall || isStandaloneSection) && sectionQueryParam
  // let sectionNames = sectionsConfig.map(s => s.type as SectionName)

  if (onlySection) {
    sectionsConfig = sectionsConfig.filter(c => c.type === onlySection)
    // sectionNames = [onlySection as SectionName]
  }

  let sections: JSX.Element[] = []

  sectionsConfig.forEach(sectionConfig => {
    if (sectionConfig.type === 'tasks') {
      sections.push(<TasksSection
        key='tasks-section'
        notificationsInfo={notificationsInfo}
        sectionConfig={sectionConfig}
      />)
    }

    if (sectionConfig.type === 'notifications') {
      sections.push(<NotificationsSection
        key='notifications'
        notificationsInfo={notificationsInfo}
        sectionConfig={sectionConfig}
      />)
    }

    if (sectionConfig.type === 'closing-documents') {
      sections.push(
        <ClosingDocumentsSection
          key="closing-documents"
          closingDocuments={compact(globalOrderData.Downloads)}
          CompanyIdOrderIdHash={order.CompanyIdOrderIdHash}
          collapsible={isSmallButNotTooSmall}
          sectionConfig={sectionConfig}
          notificationRefs={notificationsInfo.notifications}
        />
      );
    }

    if (sectionConfig.type === 'wiring-instructions') {
      sections.push(
        <WiringInstructionsSection
          key="wiring-instructions"
          wiringInstructions={order.WiringInstructions}
          CompanyIdOrderIdHash={order.CompanyIdOrderIdHash}
          collapsible={isSmallButNotTooSmall}
          sectionConfig={sectionConfig}
          hasWiringInstructionsTask={notificationsInfo.hasWiringInstructionsTask}
          wiringInstructionsTaskRef={notificationsInfo.wiringInstructionsTaskRef}
        />
      );
    }

    if (sectionConfig.type === ('closing-contacts')) {
      sections.push(<ClosingContactsSection
        key='closing-contacts'
        contacts={compact(order.ClosingContacts)}
        collapsible={isSmallButNotTooSmall}
        sectionConfig={sectionConfig}
      />)
    }

    if (sectionConfig.type.includes('CustomSection')) {
      sections.push(<CustomSection
        key={sectionConfig.type}
        sectionConfig={sectionConfig}
        collapsible={isSmallButNotTooSmall}
      />)
    }
  })

  return (
    <>
      { (isSmall && !sectionQueryParam) ? (
        <MobileSectionLinkList sectionsConfig={sectionsConfig} />
      ) : (
        <Box sx={{ flexBasis: { sm: 'auto', md: '60%' }, minWidth: 0 }}>
          <Stack spacing={2}>
            {sections}
          </Stack>
        </Box>
      )}

      <Stack sx={{ maxWidth: { sm: 'none', md: '400px'}, flexBasis: { sm: 'auto', md: '40%'} }} spacing={2}>
        { !onlySection && !isStandaloneSection && closingTrackerSectionConfig &&
          <ClosingTracker
            stepProgress={order.StepProgress}
            sectionConfig={closingTrackerSectionConfig}
          />
        }
        { (!isStandaloneSection && !isSmall && closingDetailsSectionConfig && role !== 'AdHoc') ?
          <ClosingDetailsSection sectionConfig={closingDetailsSectionConfig} />
          : ''
        }
      </Stack>
    </>
  )
}
